import {useEffect, useState, useCallback} from "react";
import {observer} from "mobx-react";
import type {SpaceFile} from "../../../../../data/models/SpaceFile";
import type {View} from "../../../../../data/models/View";
import {XyiconFeature} from "../../../../../generated/api/base";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import type {SpaceViewRenderer} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import {SearchFieldV5} from "../../../input/search/SearchFieldV5";
import {ButtonV5} from "../../../button/ButtonV5";
import {ToggleContainerV5} from "../../../widgets/ToggleContainerV5/ToggleContainerV5";
import {CheckboxInputV5} from "../../../details/datatypes/CheckboxInputV5";
import {SpaceVersionSelectorV5} from "../SpaceVersionSelectorV5";
import LayersIcon from "../../../icons/layer-icon.svg?react";
import {HeaderButtonsStyled} from "../../../filter/FilterEditorV5";
import {IconButtonV5} from "../../../interaction/IconButtonV5";
import CloseIcon from "../../../icons/xmark.svg?react";
import {LayerPanelStyled, PanelHeaderStyled} from "./LayerPanel.styled";
import {LayerViewV5} from "./LayerViewV5";
import {MarkupColorFilterPanelV5} from "./MarkupColorFilterPanelV5";

export interface IOverlayPanelProps {
	readonly view: View;
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly onClose?: () => void;
}

interface ILayerPanelProps extends IOverlayPanelProps {
	readonly showNumbers: boolean;
}

export const LayersPanelV5 = observer((props: ILayerPanelProps) => {
	const [searchString, setSearchString] = useState("");
	const {view, spaceViewRenderer, showNumbers, onClose} = props;
	const feature = XyiconFeature.SpaceEditor;
	const viewKey = `${feature}-${view?.id ?? "unknown"}`;

	const selectedSpaceEditorView = spaceViewRenderer.actions.getSelectedView(XyiconFeature.SpaceEditor);
	const spaceEditorViewSettings = view.spaceEditorViewSettings;

	const onBackgroundToggle = (toHidden: boolean) => {
		spaceEditorViewSettings.layers.background.isHidden = !toHidden;

		if (selectedSpaceEditorView === view) {
			spaceViewRenderer.tileManager.container.visible = toHidden;
			spaceViewRenderer.needsRender = true;
		}
	};

	const onSpaceVersionChange = (spaceFile: SpaceFile) => {
		const space = spaceFile.parent;

		space.setSelectedSpaceFile(spaceFile);
		spaceViewRenderer.reloadTiles();
	};

	const getSavedLayerSettings = useCallback(() => {
		return view.getSavedSpaceEditorViewSettings()?.layers || view.getDefaultLayerSettings();
	}, [view]);

	const isResetButtonEnabled = useCallback(() => {
		const layerSettings = view.spaceEditorViewSettings.layers;
		return JSON.stringify(layerSettings) !== JSON.stringify(getSavedLayerSettings());
	}, [view, getSavedLayerSettings]);

	const onResetClick = useCallback(() => {
		view.spaceEditorViewSettings.layers = getSavedLayerSettings();
		spaceViewRenderer.spaceItemController.onLayerSettingsModified();

		if (selectedSpaceEditorView === view) {
			spaceViewRenderer.tileManager.container.visible = !view.spaceEditorViewSettings.layers.background.isHidden;
		}
		sessionStorage.setItem(viewKey, "0");
	}, [view, getSavedLayerSettings, spaceViewRenderer, selectedSpaceEditorView, viewKey]);

	useEffect(() => {
		const spaceEditorViewSettings = JSON.stringify(view.spaceEditorViewSettings);
		view.applyData(view.data);
		view.setSpaceEditorViewSettings(JSON.parse(spaceEditorViewSettings));
	}, [view]);

	const backgroundTitle = "Background Layer";
	const hasChanges = selectedSpaceEditorView.hasUnsavedChanges("layers", false);

	return (
		<LayerPanelStyled>
			<div className="container">
				<PanelHeaderStyled>
					<div className="title">
						<LayersIcon />
						<h4>LAYERS</h4>
					</div>
					<HeaderButtonsStyled>
						{hasChanges && (
							<ButtonV5
								type="secondary"
								label="Reset"
								disabled={!isResetButtonEnabled()}
								onClick={onResetClick}
							/>
						)}
						<IconButtonV5
							IconComponent={CloseIcon}
							onClick={onClose}
						/>
					</HeaderButtonsStyled>
				</PanelHeaderStyled>
				<div className="head">
					<SearchFieldV5
						className="SearchField"
						fullWidth
						value={searchString}
						onInput={setSearchString}
					/>

					<div className="field hbox">
						{StringUtils.containsIgnoreCase(backgroundTitle, searchString) && (
							<>
								<div className="hbox checkbox">
									<CheckboxInputV5
										onChange={onBackgroundToggle}
										value={!spaceEditorViewSettings.layers.background.isHidden}
									/>
									<div className="title">{backgroundTitle}</div>
								</div>
								<SpaceVersionSelectorV5
									space={spaceViewRenderer.space}
									onVersionClick={onSpaceVersionChange}
								/>
							</>
						)}
					</div>
				</div>
				<div className="body">
					<ToggleContainerV5
						title="Boundaries"
						open={true}
					>
						<LayerViewV5
							view={view}
							searchString={searchString}
							spaceViewRenderer={spaceViewRenderer}
							showNumbers={showNumbers}
							activeSection="boundary"
						/>
					</ToggleContainerV5>
					<ToggleContainerV5
						title="Xyicons"
						open={true}
					>
						<LayerViewV5
							view={view}
							searchString={searchString}
							spaceViewRenderer={spaceViewRenderer}
							showNumbers={showNumbers}
							activeSection="xyicon"
						/>
					</ToggleContainerV5>
					<ToggleContainerV5
						title="Markups"
						open={true}
					>
						<MarkupColorFilterPanelV5
							spaceViewRenderer={spaceViewRenderer}
							view={view}
						/>
						<LayerViewV5
							view={view}
							searchString={searchString}
							spaceViewRenderer={spaceViewRenderer}
							showNumbers={showNumbers}
							activeSection="markup"
						/>
					</ToggleContainerV5>
				</div>
			</div>
		</LayerPanelStyled>
	);
});
